import React from 'react'
import PropTypes from 'prop-types'
import {Tooltip} from 'antd'
import {MATRIX_TONE_WORDS, matrixOf, matrixToneOf} from '../../helpers/efficiencyReport'
import Notice from '../shared/Notice'
import {DASH, formatDecimal, formatEfficiency, formatHours, formatInt} from '../../helpers/format'

/**
 * Ma trận nhân viên × công đoạn — ai mạnh ở công đoạn nào.
 *
 * ⚠️ Chỉ dựng khi người dùng ĐÃ thu hẹp bộ lọc (xem `shouldShowMatrix`). Toàn xưởng là một lưới
 *    200 × 9 không ai đọc được, và nó đẩy phần còn lại của trang xuống dưới màn hình.
 *
 * Dùng `<table>` thật chứ không `<Table>` của antd: đây là lưới ô màu cố định, không phân trang,
 * không sắp xếp, không cột co giãn — mọi thứ `<Table>` mang theo đều là thứ ở đây không dùng, còn
 * ô `<td>` thuần thì hiện được 9 cột trong chiều ngang mà `<Table>` cần cuộn.
 */
function EfficiencyMatrix({actors, stages, focusStageCode, narrowed, onSelectActor}) {
    /*
     * Chưa thu hẹp bộ lọc thì NÓI RA, không im lặng trả `null` (FR-007).
     *
     * ⚠️ Câu hướng dẫn nằm ở đây chứ không ở trang gọi, dù nó là một `Notice` chứ không phải lưới:
     *    trang đó đã 1.400 dòng và `eslint-plugin-react-hooks` của bản này đếm nhánh trên toàn hàm
     *    — thêm một ngã ba nữa vào JSX của nó là plugin tràn bộ đếm và báo NHẦM "useMemo is called
     *    conditionally" cho hai hook ở tít phía trên, không liên quan gì. Gom cả hai trạng thái của
     *    khu vực ma trận vào đúng component của khu vực ấy vừa tránh được chuyện đó vừa đúng chỗ.
     */
    if (!narrowed) {
        return (
            <Notice level="info">
                <div>
                    <strong>Ma trận nhân viên × công đoạn</strong> hiện khi đã thu hẹp bộ lọc — chọn một nhóm
                    tài khoản hoặc đúng một công đoạn rồi bấm Áp dụng.
                </div>
                <div className="VcHint">
                    Mẹo: chọn cả tổ để so người trong tổ với nhau. Lưới của toàn xưởng là 200 dòng × 9 cột,
                    không dò được bằng mắt.
                </div>
            </Notice>
        )
    }

    const rows = matrixOf(actors, stages.map((s) => s.code), focusStageCode)

    if (rows.length === 0) return null

    return (
        <div className="VcMatrix">
            <div className="VcMatrixHead">
                Ma trận nhân viên × công đoạn
                <span className="VcMuted">
                    {' '}
                    — <strong>chỉ số tương đối</strong> so với trung vị TOÀN XƯỞNG của từng công đoạn
                    (1,00 = ngang mặt bằng), bấm vào tên để khoan sâu
                </span>
            </div>
            <div className="VcMatrixScroll">
                <table className="VcMatrixTable">
                    <thead>
                        <tr>
                            <th scope="col">Tài khoản</th>
                            {stages.map((stage) => (
                                /* Lọc một công đoạn thì cột đó được tô đậm — nó là cột đang hỏi. */
                                <th
                                    scope="col"
                                    key={stage.code}
                                    className={stage.code === focusStageCode ? 'VcMatrixFocus' : ''}
                                >
                                    {stage.name || stage.code}
                                </th>
                            ))}
                            <th scope="col">Chung</th>
                            <th scope="col">
                                {/* FR-007 — số công đoạn người này làm ở mức bằng/trên mặt bằng. */}
                                Đa năng
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.actor}>
                                <th scope="row">
                                    <button type="button" className="VcLinkBtn" onClick={() => onSelectActor(row.actor)}>
                                        {row.actor}
                                    </button>
                                </th>
                                {row.cells.map((cell) => (
                                    <td
                                        key={cell.stageCode}
                                        className={cell.stageCode === focusStageCode ? 'VcMatrixFocus' : ''}
                                    >
                                        <Cell cell={cell}/>
                                    </td>
                                ))}
                                <td className="VcMatrixTotal">
                                    <Cell cell={{efficiency: row.total}}/>
                                </td>
                                <td className="VcMatrixTotal">
                                    <Versatility total={row.total} stageCount={row.cells.length}/>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

/**
 * Một ô: nền tô theo NĂM mức của chỉ số tương đối, CON SỐ luôn in ra.
 *
 * ⚠️ Con số là chỉ số tương đối (1,00 = ngang mặt bằng công đoạn), KHÔNG phải hiệu suất tuyệt đối.
 *    Bản trước in hiệu suất và tô theo hạng: công đoạn nào định mức đặt chặt thì đỏ nguyên cột, và
 *    một cột đỏ toàn bộ không giúp xếp người nào với người nào — đúng việc mà ma trận sinh ra để
 *    làm (FR-007, BR-7.2). Hiệu suất tuyệt đối vẫn còn, nằm trong tooltip.
 *
 * ⚠️ Màu KHÔNG phải kênh duy nhất (AC-UI-01). Ô trống (người này không làm công đoạn đó) để gạch
 *    ngang trên nền trung tính, phân biệt rõ với ô 0% — một bên là "không làm", bên kia là "làm mà
 *    không ra sản lượng nào", và gộp hai thứ đó là xoá mất câu hỏi đáng hỏi nhất của ma trận.
 */
const Cell = ({cell}) => {
    const eff = cell && cell.efficiency

    if (!eff || eff.notApplicable || eff.relativeIndex === null || eff.relativeIndex === undefined) {
        return <span className="VcMatrixCell VcMatrixCell--empty">{DASH}</span>
    }

    const tone = matrixToneOf(eff.relativeIndex)
    const hours = eff.allocatedHours === null || eff.allocatedHours === undefined
        ? null
        : `${formatHours(eff.allocatedHours, 1)} h phân bổ`
    const title = [
        `${MATRIX_TONE_WORDS[tone]} (${formatDecimal(eff.relativeIndex, 2)}×)`,
        eff.efficiency === null || eff.efficiency === undefined ? null : `hiệu suất ${formatEfficiency(eff.efficiency)}`,
        hours,
    ].filter(Boolean).join(' · ')

    return (
        <Tooltip title={title}>
            <span className={`VcMatrixCell VcMatrixCell--${tone}`}>{formatDecimal(eff.relativeIndex, 2)}</span>
        </Tooltip>
    )
}

/**
 * Cột Đa năng: bao nhiêu công đoạn người này làm ở mức bằng hoặc trên mặt bằng chung.
 *
 * Mẫu số là số công đoạn ĐANG HIỆN trên lưới, không phải số công đoạn họ từng làm — người đọc so
 * với đúng những cột họ đang nhìn thấy.
 */
const Versatility = ({total, stageCount}) => {
    if (!total || typeof total.versatility !== 'number') return <span className="VcZero">{DASH}</span>

    return (
        <Tooltip title={`Đạt mặt bằng ở ${formatInt(total.versatility)} trên ${formatInt(stageCount)} công đoạn đang hiện`}>
            <span className="VcHasTooltip">
                {formatInt(total.versatility)}
                <span className="VcMuted">/{formatInt(stageCount)}</span>
            </span>
        </Tooltip>
    )
}

EfficiencyMatrix.propTypes = {
    actors: PropTypes.array,
    stages: PropTypes.array.isRequired,
    /** Công đoạn đang lọc — cột được tô đậm và lưới xếp theo chỉ số tại công đoạn đó. */
    focusStageCode: PropTypes.string,
    /** Bộ lọc ĐÃ ÁP DỤNG có đủ hẹp để lưới đọc được không — xem `shouldShowMatrix`. */
    narrowed: PropTypes.bool,
    onSelectActor: PropTypes.func.isRequired,
}

export default EfficiencyMatrix
